import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AddedProvider,
  AddProviders,
  BusinessType,
  CreateUserDTO,
  DefaultProfileValues,
  ProviderAccountMap,
  RemoveProviders,
  Tenant,
  UserData,
  UserTenantsDTO,
  RegisterUserWithTenantProvidersMappingDTO
} from '@core/model';
import { HomeService } from '@core/service/home.service';
import { ProviderTypes } from '@shared/enum/provider-types';
import { removeKey } from '@shared/utils';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { EMAIL_REGEX } from '@mynexus/mynexus-ui-lib';

const ERROR_MESSAGE = `Your request could not be processed at this time. For assistance, please contact the Portal Support team by phone at 1-844-411-9622.`;
const EMAIL_ERROR_MESSAGE = 'Registration Email Notification Failed.';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    standalone: false
})
export class HomeComponent implements OnInit {
  activeIndex = 1;
  emailError: 'emailAlreadyExists' | null = null;
  tenants: Tenant[] = [];
  providerTypes: BusinessType[] = [];
  userData: UserData | null = null;
  selectedAccounts: Tenant[] = []; // Save items order
  selectedProviders: AddedProvider = {};
  providerType: BusinessType | null = null;
  isLoading = false;
  saveSuccess = false;
  portalSupportLink = 'https://app.smartsheet.com/b/form/3511b90b2c4c4aa383763192c667aba6';
  recaptchaToken: string | undefined;
  userInfoForm!: FormGroup;

  get isSingleProviderType(): boolean {
    return (
      this.providerType?.businessType === ProviderTypes.HealthPlanCaseManager ||
      this.providerType?.businessType === ProviderTypes.Physician ||
      this.providerType?.businessType === ProviderTypes.HospitalPlanner
    );
  }

  constructor(
    private homeService: HomeService,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private formbuilder: FormBuilder

  ) {}

  ngOnInit(): void {
    this.buildUserInfoForm();
    this.userInfoForm.get('preferredBusinessEmail')?.valueChanges.subscribe(() => {
    this.emailError = null;
    });
    this.getTenants();
    this.emailCtrl?.valueChanges.subscribe(() => (this.emailError = null));
  }
  
  
  private buildUserInfoForm(): void {
    this.userInfoForm = this.formbuilder.group({
      firstName: [null, [Validators.required, Validators.maxLength(50)]],
      lastName: [null, [Validators.required, Validators.maxLength(50)]],
      preferredBusinessEmail: [{ value: null, disabled: true }, [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      preferredBusinessPhone: [null, [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      preferredBusinessFax:   [null, [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      phoneNumberExtension:   [null, [Validators.maxLength(9)]],
    });
  }


  goBack() {
    this.activeIndex = this.activeIndex - 1;
  }

  goNext() {
    this.activeIndex = this.activeIndex + 1;
  }

  handleUserInfoComplete(value: any) {
    this.userData = value;
    this.userInfoForm.get('preferredBusinessEmail')?.enable({ emitEvent: false });
    this.goNext();
  }

  addProvider({ tenant, providers }: AddProviders) {
    const { tenantId } = tenant;

    this.selectedProviders = {
      ...this.selectedProviders,
      [tenantId]: {
        ...this.selectedProviders[tenantId],
        ...providers.reduce((o, p) => ({ ...o, [p.providerAccountId]: p}), {})
      }
    };
  }

  removeProviders({ tenant, providers }: RemoveProviders) {
    let accountProviders = this.selectedProviders[tenant.tenantId];

    if (accountProviders) {
      const providerKeysToRemove = providers.map(p => p.providerAccountId);
      providerKeysToRemove.forEach(
        key =>{
          accountProviders = removeKey(key, accountProviders);
          this.selectedProviders[tenant.tenantId] = accountProviders;
        }
      );
    }
  }

  addTenant(tenant) {
    const { tenantId } = tenant;

    this.selectedAccounts = [...this.selectedAccounts, tenant];
    this.selectedProviders = {
      ...this.selectedProviders,
      [tenantId]: { ...this.selectedProviders[tenantId] }
    };
  }

  removeTenant(tenant: Tenant) {
    const selectedProviders = removeKey(tenant.tenantId, this.selectedProviders);
    this.selectedAccounts = this.selectedAccounts.filter(a => a !== tenant);
    this.selectedProviders = selectedProviders;
    this.getTenants();
  }

  handleProviderTypeChange(providerType: BusinessType) {
    this.providerType = {...providerType};
  }

  handleSubmit() {
    const emailCtrl = this.emailCtrl;
    emailCtrl?.enable({ emitEvent: false });
    emailCtrl?.markAsTouched();
    emailCtrl?.updateValueAndValidity();

    if (!emailCtrl || emailCtrl.invalid) {
      return;
    }
    this.userData = {
      ...this.userData,
      preferredBusinessEmail: (emailCtrl.value || '').trim()
    };

    this.isLoading = true;
    const payload: RegisterUserWithTenantProvidersMappingDTO = {
      userProfile: this.buildUserProfile(),
      providerMapping: {
        createdBy: this.userData.preferredBusinessEmail,
        businessTypeId: this.providerType.businessTypeID,
        providerAccounts: this.isSingleProviderType
          ? this.createProviderAccounts()
          : this.createProvidersList()
      }
    };

    this.homeService.registerUserWithTenantProviders(payload, this.recaptchaToken)
      .pipe(finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (res:any) => {          
        const message = (res?.message ?? res ?? '').toString();

        if (message.toLowerCase().includes('already exist')) {
          this.emailError = 'emailAlreadyExists';
          return; 
        }

          this.saveSuccess = true;
          this.goNext();
        },
        error: (err) => {
          const message: string = (err?.error ?? '').toString();
            if (message === "EMAIL_FAILED") {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: EMAIL_ERROR_MESSAGE
            });
          } else if (message.includes("reCAPTCHA validation failed")) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: message
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: ERROR_MESSAGE
            });
          }
        }
      });
  }
  
  get emailCtrl() {
    return this.userInfoForm?.get('preferredBusinessEmail');
  }

  private createProvidersList() {
    return Object.keys(this.selectedProviders).reduce((acc, tenantId) => {
      const providers: ProviderAccountMap[] = Object.values(this.selectedProviders[tenantId]).map(
        ({ providerAccountId }) => ({
          providerAccountId,
          tenantId
        })
      );
      acc = [...acc, ...providers];
      return acc;
    }, []);
  }

  private createProviderAccounts() {
    const providers: ProviderAccountMap[] = this.selectedAccounts.map(item => {
      return {
        providerAccountId: null,
        tenantId: item.tenantId
      };
    });
    return providers;
  }

  private getTenants() {
    const { tenants, businessTypes } = this.route.snapshot.data.tenantsForRegistration as UserTenantsDTO;
    this.tenants = tenants;
    this.providerTypes = [...businessTypes];
  }

  private buildUserProfile(): CreateUserDTO {
    return {
      surname: this.userData.lastName,
      givenName: this.userData.firstName,
      displayName: `${this.userData.firstName} ${this.userData.lastName}`,
      facsimileTelephoneNumber: this.userData.preferredBusinessFax,
      telephoneNumber: this.userData.preferredBusinessPhone,
      telephoneNumberExtension: this.userData.phoneNumberExtension?.trim() === '' ? null : this.userData.phoneNumberExtension,
      signInNames: [
        {
          type: DefaultProfileValues.emailAddress,
          value: this.userData.preferredBusinessEmail
        }
      ],
      objectType: DefaultProfileValues.user,
      isExternal: true,
      deletionTimestamp: null,
      accountEnabled: false,
      assignedLicenses: [],
      assignedPlans: [],
      city: null,
      country: null,
      creationType: DefaultProfileValues.localAccount,
      department: null,
      dirSyncEnabled: null,
      immutableId: null,
      jobTitle: null,
      lastDirSyncTime: null,
      mail: null,
      mailNickname: null,
      mobile: null,
      onPremisesSecurityIdentifier: null,
      otherMails: [],
      passwordPolicies: null,
      physicalDeliveryOfficeName: null,
      postalCode: null,
      preferredLanguage: null,
      provisionedPlans: [],
      provisioningErrors: [],
      proxyAddresses: [],
      sipProxyAddress: null,
      state: null,
      streetAddress: null,
      usageLocation: null,
      userType: DefaultProfileValues.member
    };
  }
}
